import { Component, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
	INTERACTION_TYPE,
	ITEM_TYPE,
	SCOPE_TYPE,
	SUBSCOPE_TYPE,
} from 'app/interfaces/actiontracker.interface';
import { EventPrice, ICalendarEvent } from 'app/interfaces/event.interface';
import { ActionTrackerService } from 'app/services/actiontracker.service';
import { DateTimeService } from 'app/services/datetime.service';
import { EventsService } from 'app/services/events.service';
import { ZkuService } from 'app/services/zku.service';
import { ToggleEvent } from 'shared/components/accordion/accordion.component';

@Component({
	selector: 'appointment-details-payment-data',
	templateUrl: './appointment-details-payment-data.component.html',
	styleUrls: ['./appointment-details-payment-data.component.scss'],
})
export class AppointmentDetailsPaymentDataComponent implements OnInit {
	public isProfessionalZen = false;
	public eventPrice: EventPrice;
	public currentTimeout: NodeJS.Timeout;
	public shouldDisableReceiptButton = false;

	@Input()
	public event: ICalendarEvent;

	constructor(
		public translateService: TranslateService,
		public dateTimeService: DateTimeService,
		public eventService: EventsService,
		public zkuService: ZkuService,
		public actionTrackerService: ActionTrackerService
	) {}

	public ngOnInit(): void {
		this.isProfessionalZen = this.checkIsProfessionalZen();
		this.eventPrice = this.eventService.getEventPriceToShow(this.event);
	}

	public onToggleAccordion(toggleEvent: ToggleEvent): void {
		if (!toggleEvent.isOpen) {
			clearTimeout(this.currentTimeout);
			return;
		}

		this.actionTrackerService.eventTracker(
			SCOPE_TYPE.Appointment,
			SUBSCOPE_TYPE.Details,
			ITEM_TYPE.PaymentCard,
			INTERACTION_TYPE.Click
		);

		this.currentTimeout = setTimeout(() => {
			this.actionTrackerService.eventTracker(
				SCOPE_TYPE.Appointment,
				SUBSCOPE_TYPE.Details,
				ITEM_TYPE.PaymentCard,
				INTERACTION_TYPE.Seen
			);
		}, 2000);
	}

	public checkIsProfessionalZen(): boolean {
		const userService = this.zkuService.getZku();

		if (!userService) {
			return false;
		}

		return userService.tags?.some((tag) => tag['id'] === 'professional_zen');
	}

	public shouldShowEventPriceData(): boolean {
		if (!this.eventPrice) {
			return false;
		}

		if (this.event.status !== 'paid') {
			return false;
		}

		if (this.hasCompensationFinal()) {
			return false;
		}

		if (!this.hasProfessionalCompensation()) {
			return false;
		}

		if (this.isProfessionalZen && this.event.isB2C) {
			return true;
		}

		if (!this.isProfessionalZen) {
			return true;
		}

		return false;
	}

	public hasProfessionalCompensation(): boolean {
		return this.event.professionalCompensation > 0;
	}

	public hasCompensationFinal(): boolean {
		return this.event.compensationFinal?.compensation > 0;
	}

	public shouldShowReceiptButton(): boolean {
		if (!this.event) {
			return false;
		}

		if (this.dateTimeService.dateTimeIsAfter(this.event.dayEnd)) {
			return false;
		}

		if (!this.event.isB2C) {
			return false;
		}

		if (this.event.status !== 'paid') {
			return false;
		}

		return this.event.price > 0 || this.event.b2cPaidPrice > 0;
	}

	public onRequestGenerateReceipt(): void {
		this.shouldDisableReceiptButton = true;
	}

	public shouldShowEstimatedValue(): boolean {
		return this.shouldShowEventPriceData();
	}

	public shouldShowValue(): boolean {
		if (this.event.status !== 'paid') {
			return false;
		}

		return this.hasCompensationFinal();
	}

	public shouldShowExtraInfo(): boolean {
		if (this.shouldShowReceiptButton()) {
			return false;
		}

		if (!this.shouldShowEstimatedValue()) {
			return false;
		}

		return this.eventPrice.extraInfo?.length > 0;
	}

	public getPaymentStatus(): string {
		if (
			['reschedule', 'reschedule_request'].includes(
				this.event.status.toLowerCase()
			)
		) {
			return this.translateService.instant(
				'APPOINTMENTS.DETAILS.BODY.PAYMENT_DATA.PAYMENT_STATUS.PAID'
			);
		}

		return this.event.statusText;
	}

	public getSessionType(): string {
		if (
			this.event.compensationInfo?.subscription?.type ===
			'corporate_subscription'
		) {
			return this.translateService.instant('GENERAL.CORPORATE');
		} else if (this.event.origin === 'reposition') {
			return this.translateService.instant('GENERAL.REPOSITION');
		}

		return this.translateService.instant('GENERAL.PARTICULAR');
	}
}
